import React from 'react';
import { Checkbox } from '@mantine/core';
import { HOLDINGS_EVENT_TYPE_OPTIONS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import {
	DATE_FORMATS,
	formatBEDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date.ts';
import {
	eventTypes,
	findEventByType,
	getDateLabel,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/utils.ts';
import { HoldingEventRes, HoldingEventType } from '@dealstack/shared/lib/generated';
import {
	EventCardsContainer,
	EventContent,
	EventDeleteCardWrapper,
	EventField,
	EventHeader,
	FieldLabel,
	FieldValue,
	HeaderTitle,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/styled';

interface Props {
	holdingsEvents: HoldingEventRes[];
	selectedEventIds: string[];
	onToggleSelect: (eventId: string) => void;
	tCapManage: (key: string) => string;
}

export const DeleteEventCards: React.FC<Props> = ({
	holdingsEvents,
	selectedEventIds,
	onToggleSelect,
	tCapManage,
}) => {
	const eventMap = {
		[HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES]: findEventByType(
			holdingsEvents,
			HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES
		),
		[HoldingEventType.PLEDGING_OF_SHARES]: findEventByType(
			holdingsEvents,
			HoldingEventType.PLEDGING_OF_SHARES
		),
		[HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS]: findEventByType(
			holdingsEvents,
			HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS
		),
		[HoldingEventType.NOTE]: findEventByType(holdingsEvents, HoldingEventType.NOTE),
	};

	const renderEventCard = (type: HoldingEventType) => {
		const event = eventMap[type];

		if (!event) {
			return null;
		}

		const typeOption = HOLDINGS_EVENT_TYPE_OPTIONS.find((option) => option.value === type);
		const label = typeOption?.label || type;
		const isSelected = selectedEventIds.includes(event.id);

		return (
			<EventDeleteCardWrapper key={event.id} isSelected={isSelected}>
				<EventHeader>
					<HeaderTitle>{label}</HeaderTitle>
					<Checkbox
						checked={isSelected}
						onChange={() => onToggleSelect(event.id)}
						color="red"
						styles={{
							input: {
								'&:checked': {
									backgroundColor: '#f03e3e',
									borderColor: '#f03e3e',
								},
							},
						}}
					/>
				</EventHeader>

				<EventContent>
					<EventField>
						<FieldLabel>{getDateLabel(type, tCapManage)}</FieldLabel>
						{formatBEDateToUiWithFallback(event.date, DATE_FORMATS.CALENDAR_DATE)}
					</EventField>

					{type === HoldingEventType.PLEDGING_OF_SHARES && event.pledgeToString && (
						<EventField>
							<FieldLabel>{tCapManage('holdings.event.pledge_to')}:</FieldLabel>
							<FieldValue>{event.pledgeToString}</FieldValue>
						</EventField>
					)}

					{type === HoldingEventType.ASSIGNMENT_OF_VOTING_RIGHTS &&
						event.assignedToString && (
							<EventField>
								<FieldLabel>{tCapManage('holdings.event.assigned_to')}:</FieldLabel>
								<FieldValue>{event.assignedToString}</FieldValue>
							</EventField>
						)}

					{event.note && (
						<EventField>
							<FieldLabel>{tCapManage('holdings.event.note_to')}:</FieldLabel>
							<FieldValue>{event.note}</FieldValue>
						</EventField>
					)}
				</EventContent>
			</EventDeleteCardWrapper>
		);
	};

	return (
		<EventCardsContainer>{eventTypes.map((type) => renderEventCard(type))}</EventCardsContainer>
	);
};
